import { IonContent, IonLoading, IonPage, IonToast, useIonAlert } from '@ionic/react';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCompany } from '~/api/hooks/useCompany';
import { createInvoice } from '~/api/invoices';
import { queryKeys } from '~/api/queryKeys';
import { Icon } from '~/components/ui/Icon';
import { useAppNavigation } from '~/hooks/useAppNavigation';
import { clearDraft, enqueuePendingInvoice, loadDraft, saveDraft } from '~/offline/draftDb';
import { StepRevisao } from '~/pages/emitir/StepRevisao';
import { StepServico, validateServico } from '~/pages/emitir/StepServico';
import { StepTomador, validateTomador } from '~/pages/emitir/StepTomador';
import { StepValor, validateValor } from '~/pages/emitir/StepValor';
import { WizardLayout } from '~/pages/emitir/WizardLayout';
import { WizardProvider, type WizardStep } from '~/pages/emitir/wizardContext';
import { ROUTES } from '~/routes';
import {
  type InvoiceDraft,
  type ServicoInput,
  type TomadorInput,
  type ValorInput,
  buildEmptyDraft,
  buildInvoicePayload,
  ensureIdempotencyKey,
} from '~/schemas/invoice';

const DRAFT_PERSIST_DEBOUNCE_MS = 400;

export function Emitir(): JSX.Element {
  const { selected: company } = useCompany();
  const nav = useAppNavigation();

  if (!company) {
    return <NoCompanyView onBack={() => nav.replace(ROUTES.dashboard)} />;
  }

  return (
    <EmitirInner
      clientId={company.id}
      clientName={company.razao_social}
      onClose={() => nav.replace(ROUTES.dashboard)}
    />
  );
}

interface EmitirInnerProps {
  clientId: number;
  clientName: string;
  onClose(): void;
}

function EmitirInner({ clientId, clientName, onClose }: EmitirInnerProps): JSX.Element {
  const [draft, setDraft] = useState<InvoiceDraft>(() => buildEmptyDraft(clientId));
  const [step, setStep] = useState<WizardStep>(1);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const queryClient = useQueryClient();
  const nav = useAppNavigation();
  const [presentAlert] = useIonAlert();

  useEffect(() => {
    let cancelled = false;
    loadDraft(clientId).then((stored) => {
      if (cancelled) return;
      if (stored && stored.clientId === clientId) {
        setDraft(ensureIdempotencyKey(stored));
      }
      setHydrated(true);
    });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    if (!hydrated) return;
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      void saveDraft(draft);
    }, DRAFT_PERSIST_DEBOUNCE_MS);
    return () => {
      if (persistTimer.current) clearTimeout(persistTimer.current);
    };
  }, [draft, hydrated]);

  const setTomador = useCallback(
    (next: TomadorInput | ((prev: TomadorInput) => TomadorInput)): void => {
      setDraft((prev) => ({
        ...prev,
        tomador: typeof next === 'function' ? next(prev.tomador) : next,
      }));
    },
    [],
  );

  const setServico = useCallback(
    (next: ServicoInput | ((prev: ServicoInput) => ServicoInput)): void => {
      setDraft((prev) => ({
        ...prev,
        servico: typeof next === 'function' ? next(prev.servico) : next,
      }));
    },
    [],
  );

  const setValor = useCallback((next: ValorInput | ((prev: ValorInput) => ValorInput)): void => {
    setDraft((prev) => ({
      ...prev,
      valor: typeof next === 'function' ? next(prev.valor) : next,
    }));
  }, []);

  const resetDraft = useCallback((): void => {
    void clearDraft(clientId);
    setDraft(buildEmptyDraft(clientId));
    setStep(1);
    setFieldErrors({});
    setApiError(null);
  }, [clientId]);

  const stepErrors = useMemo(() => {
    if (step === 1) return validateTomador(draft.tomador);
    if (step === 2) return validateServico(draft.servico);
    if (step === 3) return validateValor(draft.valor);
    return {};
  }, [step, draft.tomador, draft.servico, draft.valor]);

  const canAdvance = useMemo(() => {
    if (step === 4) return true;
    return Object.keys(stepErrors).length === 0;
  }, [step, stepErrors]);

  const handleAdvance = useCallback(async () => {
    if (step < 4) {
      const errors = stepErrors;
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }
      setFieldErrors({});
      setStep((current) => (current + 1) as WizardStep);
      return;
    }

    // Step 4 — emitir definitivamente.
    setSubmitting(true);
    setApiError(null);
    try {
      const payload = buildInvoicePayload(draft);
      // A chave vem do rascunho, não é criada aqui: se ela nascesse a cada envio, a
      // retentativa chegaria ao backend como uma nota nova e duplicaria a emissão.
      const created = await createInvoice(payload, {
        asDraft: false,
        idempotencyKey: draft.idempotencyKey,
      });
      await clearDraft(clientId);
      await queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      setToast(`Nota #${created.id_integracao} enfileirada para processamento.`);
      setTimeout(() => nav.replace(ROUTES.notas), 800);
    } catch (error) {
      // Falha de rede ou usuário offline → enfileira pra drain automático.
      // Erros de validação (4xx) NÃO vão pra fila — usuário precisa corrigir.
      if (isNetworkFailure(error)) {
        try {
          await enqueuePendingInvoice(clientId, buildInvoicePayload(draft), draft.idempotencyKey);
          await clearDraft(clientId);
          setToast('Sem conexão — salva offline e será enviada quando voltar a rede.');
          setTimeout(() => nav.replace(ROUTES.notas), 1100);
        } catch (queueError) {
          setApiError(extractApiError(queueError));
        }
      } else {
        setApiError(extractApiError(error));
      }
    } finally {
      setSubmitting(false);
    }
  }, [step, stepErrors, draft, clientId, queryClient, nav]);

  const handleBack = useCallback((): void => {
    if (step === 1) {
      if (isDraftDirty(draft)) {
        void presentAlert({
          header: 'Sair do rascunho?',
          message: 'Seus dados ficam salvos pra quando você voltar.',
          buttons: [
            { text: 'Continuar emitindo', role: 'cancel' },
            {
              text: 'Sair',
              role: 'destructive',
              handler: () => onClose(),
            },
          ],
        });
        return;
      }
      onClose();
      return;
    }
    setFieldErrors({});
    setApiError(null);
    setStep((current) => (current - 1) as WizardStep);
  }, [step, draft, onClose, presentAlert]);

  const renderStep = (): JSX.Element => {
    if (step === 1) return <StepTomador fieldErrors={fieldErrors} />;
    if (step === 2) return <StepServico fieldErrors={fieldErrors} />;
    if (step === 3) return <StepValor fieldErrors={fieldErrors} />;
    return <StepRevisao apiError={apiError} />;
  };

  return (
    <IonPage>
      <IonContent fullscreen>
        <WizardProvider
          step={step}
          setStep={setStep}
          draft={draft}
          setTomador={setTomador}
          setServico={setServico}
          setValor={setValor}
          resetDraft={resetDraft}
        >
          <WizardLayout
            eyebrow={`Operando como ${clientName}`}
            title={STEP_HEADERS[step].title}
            description={STEP_HEADERS[step].description}
            canAdvance={canAdvance}
            advanceLabel={step === 4 ? 'Emitir NFSe' : 'Continuar'}
            advanceIcon={step === 4 ? 'check' : 'arrow_right_long'}
            isAdvancing={submitting}
            onAdvance={handleAdvance}
            onBack={handleBack}
          >
            {renderStep()}
          </WizardLayout>
        </WizardProvider>

        <IonLoading isOpen={submitting} message="Enviando à prefeitura..." spinner="dots" />

        <IonToast
          isOpen={Boolean(toast)}
          message={toast ?? ''}
          duration={3000}
          color="success"
          onDidDismiss={() => setToast(null)}
        />
      </IonContent>
    </IonPage>
  );
}

const STEP_HEADERS: Record<WizardStep, { title: string; description?: string }> = {
  1: {
    title: 'Quem é o tomador?',
    description: 'CNPJ busca a empresa automaticamente. CPF entra manualmente.',
  },
  2: {
    title: 'O que foi prestado?',
    description: 'Escolha o código e descreva o serviço.',
  },
  3: {
    title: 'Quanto e como tributar?',
    description: 'Preencha valor e configure ISS e retenções.',
  },
  4: {
    title: 'Confira antes de enviar.',
    description: 'Os dados vão direto para a prefeitura.',
  },
};

function isDraftDirty(draft: InvoiceDraft): boolean {
  return (
    draft.tomador.cpf_cnpj.length > 0 ||
    draft.tomador.razao_social.length > 0 ||
    draft.servico.codigo_servico.length > 0 ||
    draft.servico.discriminacao.length > 0 ||
    draft.valor.valor_servico > 0
  );
}

function isNetworkFailure(error: unknown): boolean {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true;
  if (error instanceof AxiosError) {
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') return true;
    // Sem response = nunca chegou ao backend
    if (!error.response) return true;
  }
  return false;
}

function extractApiError(error: unknown): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data;
    if (typeof data === 'string') return data;
    if (data && typeof data === 'object') {
      // O DRF devolve {field: [msg]} ou {detail: msg}. Achata em uma string legível.
      const flattened: string[] = [];
      for (const [field, value] of Object.entries(data as Record<string, unknown>)) {
        const text = Array.isArray(value)
          ? value.join(' ')
          : typeof value === 'string'
            ? value
            : JSON.stringify(value);
        flattened.push(field === 'detail' ? text : `${field}: ${text}`);
      }
      if (flattened.length > 0) return flattened.join('\n');
    }
    if (error.code === 'ERR_NETWORK') return 'Sem conexão. Tente novamente.';
    return `Erro ${error.response?.status ?? ''}`.trim();
  }
  return 'Erro inesperado ao enviar.';
}

function NoCompanyView({ onBack }: { onBack(): void }): JSX.Element {
  return (
    <IonPage>
      <IonContent fullscreen>
        <div
          className="rx-container rx-screen"
          style={{ padding: '48px 24px', textAlign: 'center' }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 52,
              height: 52,
              borderRadius: 26,
              background: 'var(--rx-surface-alt)',
              color: 'var(--rx-text-2)',
            }}
          >
            <Icon name="ledger" size={22} />
          </div>
          <p
            style={{
              marginTop: 14,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.6px',
              textTransform: 'uppercase',
              color: 'var(--rx-text-3)',
            }}
          >
            Sem empresa vinculada
          </p>
          <h1
            style={{
              marginTop: 8,
              fontSize: 24,
              fontWeight: 500,
              color: 'var(--rx-text)',
              letterSpacing: '-0.5px',
            }}
          >
            Nada para emitir.
          </h1>
          <p
            style={{
              marginTop: 8,
              fontSize: 13,
              color: 'var(--rx-text-2)',
              maxWidth: '34ch',
              marginInline: 'auto',
            }}
          >
            Peça ao seu contador para vincular uma empresa ao seu acesso.
          </p>
          <button
            type="button"
            onClick={onBack}
            style={{
              marginTop: 18,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '10px 16px',
              borderRadius: 999,
              background: 'var(--rx-brand-tint)',
              color: 'var(--rx-brand)',
              fontSize: 13,
              fontWeight: 500,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <Icon name="back" size={14} />
            Voltar ao painel
          </button>
        </div>
      </IonContent>
    </IonPage>
  );
}
